// The live volunteer and withdraw routes (bead hf-e6-commitments-coverage-udt.2).
//
// Subscription and mutation plumbing. Every word, every control and every decision
// about what a refusal means belongs to `VolunteerSurfaces.tsx` and to
// `features/claim`.
//
// ── IT PRE-CHECKS NOTHING ───────────────────────────────────────────────────────
//
// The bead is explicit and it is worth restating at the one place somebody would be
// tempted to add it: this container does not compare capacity, does not compare a
// revision, and does not decide whether a claim would succeed. It sends the tap and
// renders the answer. Atomic claiming happens in one transaction in
// `convex/domain/claimGuard.ts`, and the gap between a browser's check and the
// server's is exactly where a double-booking lives.
//
// ── HOW IT KNOWS WHOSE COMMITMENT IT IS ─────────────────────────────────────────
//
// `listMyCommitments` returns ONLY the caller's own commitments — the server scopes
// it by the resolved Membership's person. So "is this mine to withdraw?" is answered
// by data the server already narrowed, not by a permission the browser inferred. No
// extra affordance flag is needed for withdrawal, and COM-010's `not_responsible`
// remains the server's answer if anything ever slips through.
//
// Offering to help is different: `commitment.accept.self` is a capability, so it
// arrives as a server-decided flag from the plan read model and is never guessed.

import type { commitments as contracts } from "@herefor/contracts";
import { useMutation, useQuery } from "convex/react";
import { useCallback, useMemo } from "react";

import { commitmentMutations, commitmentQueries } from "../../convex/commitments";
import { useConnectivity } from "../../offline/offline";
import { useClaim, type AdjacentNeed } from "../claim";
import { CommitmentStatus, VolunteerOffer, WithdrawCommitment } from "./VolunteerSurfaces";
import { useWithdraw, type WithdrawalReasonCode } from "./useWithdraw";

/** Commitment states in which somebody is currently counted as helping. */
const LIVE_COMMITMENT_STATES = ["accepted", "reconfirmation_required"] as const;

export interface VolunteerRouteProps {
  readonly circleId: string;
  readonly supportNeedId: string;
  /** The revision the person is looking at. Sent with the claim, never re-derived. */
  readonly needRevision: number;
  /** What they would be taking on, already worded by the caller. */
  readonly responsibility: string;
  /** Server-decided: may this Membership offer to help? Never inferred here. */
  readonly canVolunteer: boolean;
  /** The people already helping, excluding the viewer. */
  readonly others: readonly {
    readonly personId: string;
    readonly name: string;
    readonly recordedByName: string | null;
    readonly agreementSource: string | null;
  }[];
  /** Server-derived; the browser never decides whether a need still wants somebody. */
  readonly stillNeedsSomeone: boolean;
  /** Other open needs, offered warmly after a race loss. */
  readonly stillNeeded?: readonly AdjacentNeed[];
  readonly onViewPlan?: () => void;
}

export function VolunteerRoute({
  circleId,
  supportNeedId,
  needRevision,
  responsibility,
  canVolunteer,
  others,
  stillNeedsSomeone,
  stillNeeded,
  onViewPlan,
}: VolunteerRouteProps) {
  const mine = useQuery(commitmentQueries.listMine, { circleId });
  const volunteer = useMutation(commitmentMutations.volunteer);
  const withdraw = useMutation(commitmentMutations.withdraw);
  const { actionsPaused } = useConnectivity();

  /**
   * The caller's own live commitment to THIS need, if there is one.
   *
   * Withdrawn and cancelled commitments are deliberately excluded rather than shown
   * as history: this surface is about what a person may do next, and a record of
   * having stepped back last week is not an affordance. The history itself is
   * preserved — invariant 6 — it simply is not this component's subject.
   */
  const ownCommitment = useMemo(
    () =>
      (mine ?? []).find(
        (row) =>
          row.supportNeedId === supportNeedId &&
          (LIVE_COMMITMENT_STATES as readonly string[]).includes(row.state),
      ),
    [mine, supportNeedId],
  );

  const claim = useClaim({
    claim: useCallback(async (): Promise<contracts.AcceptNeedResult> => {
      if (actionsPaused) {
        throw new Error("Connection is not ready for a commitment change.");
      }
      // A fresh key per attempt: the contract requires one so two taps on a flaky
      // connection cannot produce two commitments, and so the loser of a genuine race
      // gets `capacity_full` rather than a duplicate.
      const outcome = await volunteer({
        circleId,
        supportNeedId,
        needRevision,
        idempotencyKey: crypto.randomUUID(),
      });
      return unwrapClaim(outcome);
    }, [actionsPaused, volunteer, circleId, supportNeedId, needRevision]),
  });

  const stepBack = useWithdraw({
    withdraw: useCallback(
      async (reasonCode?: WithdrawalReasonCode): Promise<contracts.WithdrawCommitmentResult> => {
        if (actionsPaused) {
          throw new Error("Connection is not ready for a commitment change.");
        }
        if (ownCommitment === undefined) {
          // Unreachable while the control is only rendered with a commitment in hand.
          // Answered rather than thrown, so a race against a concurrent withdrawal
          // reads as an ordinary refusal instead of a broken screen.
          return { ok: false, rejection: { kind: "not_responsible" } };
        }
        const outcome = await withdraw({
          circleId,
          commitmentId: ownCommitment.commitmentId,
          ...(reasonCode === undefined ? {} : { reasonCode }),
        });
        return unwrapWithdraw(outcome, ownCommitment.commitmentId);
      },
      [actionsPaused, withdraw, circleId, ownCommitment],
    ),
  });

  // Nothing is rendered until the server has said what this person already agreed to.
  // Showing an offer and then replacing it with a withdrawal under somebody's cursor
  // is how a calm surface becomes a confusing one.
  if (mine === undefined) return null;

  return (
    <>
      <CommitmentStatus
        others={others}
        includesYou={ownCommitment !== undefined}
        stillNeedsSomeone={stillNeedsSomeone}
      />

      {/* THE OUTCOMES ARE RENDERED ABOVE THE AFFORDANCE, SEPARATELY FROM IT.
          This looks like indirection and is not. Both mutations change what
          `listMyCommitments` reports moments later, which flips this surface between
          the offer and the withdrawal. If an outcome lived inside the control that
          produced it, it would unmount with that control — somebody would tap "I can
          do this" and watch the panel telling them they are down to help, and that
          the family can see their name, disappear before they had read it. The same
          in reverse for stepping back. The reassurance IS the point of those panels,
          and a reassurance that flickers is worse than none. */}
      {claim.state.status === "accepted" || claim.state.status === "refused" ? (
        <VolunteerOffer
          responsibility={responsibility}
          canOffer={false}
          state={claim.state}
          onOffer={() => {
            void claim.submit();
          }}
          {...(onViewPlan === undefined ? {} : { onViewPlan })}
          {...(stillNeeded === undefined ? {} : { stillNeeded })}
        />
      ) : null}

      {stepBack.state.status === "withdrawn" || stepBack.state.status === "refused" ? (
        <WithdrawCommitment
          canWithdraw={false}
          state={stepBack.state}
          onWithdraw={() => undefined}
          {...(onViewPlan === undefined ? {} : { onViewPlan })}
        />
      ) : null}

      {/* And the live affordance, chosen from what the SERVER says this person has
          already agreed to — never from what they just did in this tab. */}
      {ownCommitment === undefined ? (
        <VolunteerOffer
          responsibility={responsibility}
          canOffer={canVolunteer && stillNeedsSomeone && claim.state.status !== "accepted"}
          actionsDisabled={actionsPaused}
          state={{ status: claim.state.status === "submitting" ? "submitting" : "idle" }}
          onOffer={() => {
            void claim.submit();
          }}
        />
      ) : (
        <WithdrawCommitment
          canWithdraw={stepBack.state.status !== "withdrawn"}
          actionsDisabled={actionsPaused}
          state={{ status: stepBack.state.status === "submitting" ? "submitting" : "idle" }}
          onWithdraw={(reasonCode) => {
            void stepBack.submit(reasonCode);
          }}
        />
      )}
    </>
  );
}

/**
 * Unwrap the boundary's `MutationOutcome` into the domain result.
 *
 * A wrapped mutation returns `{ status: "applied" | "replayed" }`. A REPLAY means the
 * idempotency key had already been seen: the write happened exactly once, and this
 * call changed nothing. The Convex client retries on reconnect, so it is reachable.
 *
 * ## Why a replay reports `already_committed` rather than success
 *
 * The replay record carries a result code, not the coverage counts — those were
 * derived in the transaction that actually ran. A claim rendered as `accepted` needs
 * `confirmedCapacity` and `requiredCapacity` to say "2 of 2 people confirmed", and
 * inventing them would put a fabricated number in front of somebody deciding whether
 * a hospital run is covered.
 *
 * `already_committed` is not a consolation shape, it is the true one:
 * `REJECTION_MESSAGES.ALREADY_COMMITTED` reads "You're already helping with this.
 * Your help is already recorded. There is nothing else you need to do." — which is
 * precisely what a replay means. The reactive `listMyCommitments` query then flips
 * the surface to the withdrawal affordance on its own, from the server's truth
 * rather than from a guess made here.
 */
function unwrapClaim(
  outcome:
    | { readonly status: "applied"; readonly value: contracts.AcceptNeedResult }
    | { readonly status: "replayed" },
): contracts.AcceptNeedResult {
  return outcome.status === "applied"
    ? outcome.value
    : { ok: false, rejection: { kind: "already_committed" } };
}

/**
 * The same unwrap for a withdrawal, where a replay IS simply success.
 *
 * Withdrawing carries no derived counts to report, so a replayed withdrawal and an
 * applied one are indistinguishable to the person: either way they are no longer
 * marked as doing it, which is the only fact the surface states.
 */
function unwrapWithdraw(
  outcome:
    | { readonly status: "applied"; readonly value: contracts.WithdrawCommitmentResult }
    | { readonly status: "replayed" },
  commitmentId: string,
): contracts.WithdrawCommitmentResult {
  return outcome.status === "applied" ? outcome.value : { ok: true, value: { commitmentId } };
}
